import { useMemo, useRef, useState } from 'react'

import ArrowDownIcon from '@icons/chevron-down'
import { useOnClickOutside } from '@/lib/hooks/useOnClickOutside'
import { useAuth } from '@/lib/provider/AuthProvider'
import { useLoadStatus } from '@/lib/queries/App.queries'
import { cn } from '@/lib/misc/Utils'
import StatusPopoverBody from './status-indicator/StatusPopoverBody'
import type {
  CombinedStatusResponse,
  StatusFeedEntry,
  StatusLevel,
  StatusReportWithTimeline,
} from './status-indicator/types'
import {
  computeDisplayLevel,
  getStatusLevelStyles,
  parseStatusFeedSections,
} from './status-indicator/utils'

const StatusIndicator = ({
  variant,
  withSupport = false,
  className,
}: {
  variant: 'desktop' | 'mobile' | 'dropdown' | 'dot'
  withSupport?: boolean
  className?: string
}) => {
  const [authState] = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const statusQuery = useLoadStatus()
  const combined: CombinedStatusResponse | null = statusQuery.data ?? null

  const parsedStatus = useMemo(() => {
    if (combined?.feed == null) {
      return {
        reportEntries: [] as StatusReportWithTimeline[],
        maintenanceEntries: [] as StatusFeedEntry[],
        feedUpdatedAt: null as string | null,
      }
    }

    const parsed = parseStatusFeedSections(combined.feed)
    return {
      reportEntries: parsed.reports,
      maintenanceEntries: parsed.maintenances,
      feedUpdatedAt: parsed.feedUpdatedAt,
    }
  }, [combined])

  const reportEntries = parsedStatus.reportEntries
  const maintenanceEntries = parsedStatus.maintenanceEntries
  const feedUpdatedAt = parsedStatus.feedUpdatedAt
  const isLoading = statusQuery.isLoading
  const hasMissingFeed = statusQuery.isSuccess && combined?.feed == null
  const hasError = statusQuery.isError || hasMissingFeed

  useOnClickOutside(dropdownRef, () => setIsOpen(false))

  const level: StatusLevel = hasError
    ? 'unknown'
    : computeDisplayLevel(reportEntries, maintenanceEntries, combined)

  const hasActiveFeed =
    reportEntries.length > 0 || maintenanceEntries.length > 0

  const isAllQuiet = !isLoading && !hasError && !hasActiveFeed

  /** Full summary header hidden: single clear “all good” state with link to public history. */
  const showAllGoodPopoverLayout =
    isAllQuiet &&
    level === 'operational' &&
    !combined?.has_ongoing_maintenance &&
    !combined?.has_planned_maintenance

  const latestTimestamp = (() => {
    const dates = [
      ...reportEntries.map((r) => r.sortDate),
      ...maintenanceEntries.map((m) => m.date),
    ].filter(Boolean)
    if (dates.length === 0) return feedUpdatedAt
    return dates.reduce((best, d) =>
      new Date(d).getTime() > new Date(best).getTime() ? d : best
    )
  })()

  const levelStyles = getStatusLevelStyles(level as StatusLevel)

  const label = isLoading
    ? 'Checking status\u2026'
    : hasError
      ? 'Status unavailable'
      : levelStyles.label

  const badgeBaseClasses = cn(
    'inline-flex items-center rounded-full text-sm font-medium cursor-pointer backdrop-blur-md transition transform hover:scale-[1.02] active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-1/80',
    'gap-2 px-3 py-1'
  )

  const layoutClasses =
    variant === 'desktop'
      ? 'ml-2 min-w-0 shrink sm:ml-4'
      : variant === 'dot'
        ? ''
        : variant === 'mobile'
          ? 'mx-auto mt-2 max-w-[260px] justify-center'
          : 'mx-auto mt-1 mb-4 max-w-[320px] justify-center'

  const triggerClasses =
    variant === 'dot'
      ? `relative inline-flex size-5 shrink-0 rounded-full border border-white/50 bg-white/10 p-0 transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 ${isOpen ? 'ring-2 ring-white/70' : ''}`
      : cn(badgeBaseClasses, levelStyles.badgeClass)

  const desktopLabelClass =
    variant === 'desktop'
      ? 'min-w-0 truncate max-w-[6rem] sm:max-w-[11rem] md:max-w-[14rem] lg:max-w-[18rem] xl:max-w-none hidden lg:block'
      : 'whitespace-nowrap'

  const userEmail = authState.user?.email?.trim() ?? ''
  const statusEmailSubscription = {
    visible: authState.active && userEmail.length > 0,
    userEmail,
  }

  return (
    <div className={cn('relative', layoutClasses, className)} ref={dropdownRef}>
      {/* Trigger badge */}
      <button
        type="button"
        className={cn(
          triggerClasses,
          variant === 'desktop' &&
            'relative min-w-0 max-w-full size-8 gap-0 p-0 lg:size-auto lg:gap-2 lg:px-3 lg:py-1'
        )}
        onClick={() => setIsOpen((open) => !open)}
        aria-label={variant === 'dot' ? label : undefined}
      >
        <span
          className={cn(
            'block size-2 shrink-0 rounded-full shadow-inner origin-center',
            levelStyles.dotClass,
            (statusQuery.isFetching || statusQuery.isLoading) &&
              'animate-[pulse_1.6s_ease-in-out_infinite]',
            variant === 'dot' &&
              'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
            variant === 'desktop' &&
              'max-lg:absolute max-lg:left-1/2 max-lg:top-1/2 max-lg:-translate-x-1/2 max-lg:-translate-y-1/2 lg:static lg:translate-none'
          )}
        />
        {variant !== 'dot' && (
          <span className={desktopLabelClass}>{label}</span>
        )}
        {variant !== 'dot' && (
          <ArrowDownIcon className="h-3 w-3 opacity-70 hidden lg:block" />
        )}
      </button>

      {isOpen && (
        <div
          className={cn(
            'absolute z-60 mt-2 overflow-hidden rounded-2xl shadow-[0_20px_50px_-8px_rgba(15,23,42,0.22),0_0_0_1px_rgba(15,23,42,0.07)]',
            withSupport
              ? 'w-[min(100vw-1.5rem,52rem)]'
              : 'w-[min(100vw-1.5rem,28rem)]',
            variant === 'desktop'
              ? 'left-0 right-auto'
              : variant === 'dot'
                ? 'left-auto right-0'
                : 'left-1/2 -translate-x-1/2'
          )}
          role="dialog"
          aria-label="Service status and support"
        >
          <StatusPopoverBody
            isPopoverOpen={isOpen}
            statusEmailSubscription={statusEmailSubscription}
            withSupport={withSupport}
            showAllGoodPopoverLayout={showAllGoodPopoverLayout}
            latestTimestamp={latestTimestamp}
            level={level}
            isLoading={isLoading}
            hasError={hasError}
            combined={combined}
            reportEntries={reportEntries}
            maintenanceEntries={maintenanceEntries}
            hasActiveFeed={hasActiveFeed}
            isAllQuiet={isAllQuiet}
          />
        </div>
      )}
    </div>
  )
}

export default StatusIndicator
